import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';
import { CountryFlag } from '../components/CountryFlag.jsx';

/**
 * Support Queries page. Two-pane layout:
 *
 *   left  — paginated, filterable list of tickets (OPEN / PENDING /
 *           CLOSED / all). Sorted by lastMessageAt desc.
 *   right — selected ticket's full message thread (user + staff
 *           alternating bubbles) with a reply composer + a Close
 *           button.
 *
 * Workflow encoded by the server:
 *   - OPEN:    user posted, waiting for staff
 *   - PENDING: staff replied, waiting for user
 *   - CLOSED:  manually closed by staff
 *
 * Replying transitions OPEN → PENDING automatically. The user's
 * next message (handled by the public Support API) flips it back
 * to OPEN. Close button forces CLOSED, regardless of state.
 *
 * @author <lukman@zshift.io>
 */
export function Queries() {
  const { me } = useAdminAuth();
  const { toast, confirm, prompt } = useUI();

  const [items,   setItems]   = useState([]);
  const [counts,  setCounts]  = useState({ open: 0, pending: 0, closed: 0 });
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [loading, setLoading] = useState(true);
  const [status,  setStatus]  = useState('OPEN');   // 'OPEN'|'PENDING'|'CLOSED'|'ALL'
  const SIZE = 30;

  const [selectedId, setSelectedId] = useState(null);
  const [thread,     setThread]     = useState(null);    // { ticket, messages }
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyDraft,    setReplyDraft]    = useState('');
  const [pendingImages, setPendingImages] = useState([]);   // staged R2 URLs
  const [uploading,     setUploading]     = useState(false);
  const [sending,       setSending]       = useState(false);

  const canReply = me?.role === 'SUPERADMIN'
                || me?.role === 'SUPPORT_LEAD'
                || me?.role === 'SUPPORT_AGENT';

  // Anchored ref so the thread auto-scrolls to the newest message
  // after a reply lands.
  const threadEndRef = useRef(null);

  // ── List fetch (with debounce-friendly request id) ────────────
  const reqIdRef = useRef(0);
  const fetchList = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('size', String(SIZE));
      if (status && status !== 'ALL') params.set('status', status);
      const res = await api('/api/admin/queries?' + params.toString());
      if (reqId !== reqIdRef.current) return;       // stale
      setItems(res?.items || []);
      setTotal(res?.total || 0);
      setCounts(res?.counts || { open: 0, pending: 0, closed: 0 });
      // Auto-select the first ticket if we don't have one yet
      // (or the current selection isn't in the new list).
      if (res?.items?.length && (selectedId == null || !res.items.find((it) => it.id === selectedId))) {
        setSelectedId(res.items[0].id);
      }
    } catch (e) {
      console.warn(e);
      toast.error(e.message || 'Could not load queries.');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Reset page when filter changes (otherwise we might land on
  // "page 5 of a 2-page result").
  useEffect(() => {
    if (page !== 0) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // ── Thread fetch ──────────────────────────────────────────────
  useEffect(() => {
    if (selectedId == null) { setThread(null); return; }
    let cancelled = false;
    setThreadLoading(true);
    api(`/api/admin/queries/${selectedId}`)
      .then((res) => {
        if (cancelled) return;
        setThread(res);
        setThreadLoading(false);
        // Scroll to bottom after layout settles.
        setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' }), 0);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e.message || 'Could not load thread.');
        setThreadLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ── Action: upload an image (stages it on the composer) ───────
  const uploadImages = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        if (!f.type?.startsWith('image/')) continue;
        const fd = new FormData();
        fd.append('file', f);
        try {
          const res = await api('/api/admin/support/upload-image', {
            method: 'POST', body: fd, multipart: true,
          });
          if (res?.url) urls.push(res.url);
        } catch (e) {
          toast.error(`Upload failed for ${f.name}: ${e?.message || e}`);
        }
      }
      if (urls.length > 0) {
        setPendingImages((prev) => [...prev, ...urls].slice(0, 12));
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Action: send a reply ──────────────────────────────────────
  const sendReply = async () => {
    const text = replyDraft.trim();
    // Reply can be image-only — body OR images is fine, both empty is not.
    if (!selectedId) return;
    if (!text && pendingImages.length === 0) return;
    setSending(true);
    try {
      const res = await api(`/api/admin/queries/${selectedId}/reply`, {
        method: 'POST',
        body: { body: text, imageUrls: pendingImages },
      });
      setThread(res);
      setReplyDraft('');
      setPendingImages([]);
      toast.success('Reply sent — ticket marked Pending.');
      // Update the list-item status inline so the user doesn't need
      // a refresh to see the OPEN → PENDING transition.
      setItems((arr) => arr.map((it) => it.id === selectedId
        ? { ...it, status: 'PENDING', lastMessageAt: new Date().toISOString() }
        : it
      ));
      setCounts((c) => ({ ...c,
        open:    Math.max(0, c.open - (thread?.ticket?.status === 'OPEN' ? 1 : 0)),
        pending: c.pending + (thread?.ticket?.status === 'OPEN' ? 1 : 0),
      }));
      // Nudge the sidebar's open-tickets badge to re-poll now — the
      // hook listens for this custom event and skips the 30s wait so
      // the badge drops the moment the admin's reply lands.
      window.dispatchEvent(new CustomEvent('noteshq:queries-counts-refresh'));
      // Scroll to the new message.
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
    } catch (e) {
      toast.error(e.message || 'Reply failed.');
    } finally {
      setSending(false);
    }
  };

  // ── Action: close ─────────────────────────────────────────────
  const closeTicket = async () => {
    if (!selectedId) return;
    const ok = await confirm({
      title: 'Close this ticket?',
      message: 'The user can still see the conversation but won\'t be able to reply unless they open a new ticket. Confirm to close.',
      confirmLabel: 'Close ticket',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await api(`/api/admin/queries/${selectedId}/close`, { method: 'POST' });
      setThread(res);
      setItems((arr) => arr.map((it) => it.id === selectedId
        ? { ...it, status: 'CLOSED' }
        : it
      ));
      // Refresh sidebar badge — closing flips the ticket out of OPEN
      // (or PENDING) into CLOSED, so the open-count may drop.
      window.dispatchEvent(new CustomEvent('noteshq:queries-counts-refresh'));
      toast.success('Ticket closed.');
    } catch (e) {
      toast.error(e.message || 'Close failed.');
    }
  };

  // ── Canned response inserter ──────────────────────────────────
  const insertCanned = (snippet) => {
    setReplyDraft((d) => d ? d + '\n\n' + snippet : snippet);
  };

  const selectedItem = useMemo(
    () => items.find((it) => it.id === selectedId) || thread?.ticket || null,
    [items, selectedId, thread]
  );

  // ── Keyboard shortcuts: ⌘/Ctrl+Enter to send ──────────────────
  const onReplyKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      sendReply();
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Support <em>queries</em></h1>
          <p className="page-sub">
            Reply, close, escalate. Every reply transitions the ticket to Pending; replies are logged in the audit trail.
          </p>
        </div>
      </div>

      <div className="card">
        {/* Filter bar — same .filterbar pattern as Users */}
        <div className="filterbar">
          <div className="seg">
            <SegBtn on={status === 'OPEN'}    onClick={() => setStatus('OPEN')}>
              Open<span className="cnt">{counts.open}</span>
            </SegBtn>
            <SegBtn on={status === 'PENDING'} onClick={() => setStatus('PENDING')}>
              Pending<span className="cnt">{counts.pending}</span>
            </SegBtn>
            <SegBtn on={status === 'CLOSED'}  onClick={() => setStatus('CLOSED')}>
              Closed<span className="cnt">{counts.closed}</span>
            </SegBtn>
            <SegBtn on={status === 'ALL'}     onClick={() => setStatus('ALL')}>
              All
            </SegBtn>
          </div>
          <div style={{ marginLeft: 'auto', fontFamily: 'Geist Mono, monospace', fontSize: 11.5, color: 'var(--ink-3)' }}>
            {loading ? '…' : `${total.toLocaleString()} ticket${total === 1 ? '' : 's'}`}
          </div>
        </div>

        <div className="qsplit">
          {/* ── Left: list ─────────────────────────────────────── */}
          <div className="qlist">
            {loading && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                No tickets match this filter.
              </div>
            )}
            {!loading && items.map((it) => (
              <div
                key={it.id}
                className={`qrow ${it.id === selectedId ? 'on' : ''} ${it.status === 'OPEN' ? 'unread' : ''}`}
                onClick={() => setSelectedId(it.id)}
              >
                <div className={`av v${(it.id % 10) + 1}`} style={{ width: 32, height: 32, fontSize: 12 }}>
                  {initials(it.user?.name || it.user?.email)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="qtitle">{it.subject || '(No subject)'}</div>
                  <div className="qpre">{it.preview || ' '}</div>
                  <div className="qmeta">
                    <StatusPill status={it.status} />
                    {it.user?.tier && (
                      <span className={`pill ${it.user.tier === 'PREMIUM' ? 'plus' : 'free'}`} style={{ fontSize: 10 }}>
                        {it.user.tier}
                      </span>
                    )}
                    <span className="qtime">{agoString(it.lastMessageAt)}</span>
                  </div>
                </div>
              </div>
            ))}
            {/* Pager */}
            {!loading && total > SIZE && (
              <div className="pager" style={{ borderTop: 'none' }}>
                <span className="pager-info">
                  {page * SIZE + 1}–{Math.min((page + 1) * SIZE, total)} of {total.toLocaleString()}
                </span>
                <div className="pager-buttons">
                  <button className="btn sm ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← Prev</button>
                  <button className="btn sm ghost" disabled={(page + 1) * SIZE >= total} onClick={() => setPage((p) => p + 1)}>Next →</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Right: thread ──────────────────────────────────── */}
          <ThreadView
            ticketItem={selectedItem}
            thread={thread}
            loading={threadLoading}
            replyDraft={replyDraft}
            setReplyDraft={setReplyDraft}
            sendReply={sendReply}
            sending={sending}
            closeTicket={closeTicket}
            insertCanned={insertCanned}
            canReply={canReply}
            onReplyKey={onReplyKey}
            threadEndRef={threadEndRef}
            pendingImages={pendingImages}
            uploadImages={uploadImages}
            removePendingImage={(u) => setPendingImages((p) => p.filter((x) => x !== u))}
            uploading={uploading}
          />
        </div>
      </div>
    </div>
  );
}

// ── Subcomponents ───────────────────────────────────────────────
function SegBtn({ on, onClick, children }) {
  return <button className={on ? 'on' : ''} onClick={onClick}>{children}</button>;
}

function StatusPill({ status }) {
  const cls = status === 'OPEN'    ? 'warn'
            : status === 'PENDING' ? 'info'
            : status === 'CLOSED'  ? 'muted'
            : 'muted';
  const label = status || '—';
  return (
    <span className={`pill ${cls}`} style={{ fontSize: 10 }}>
      <span className="dot"/> {label}
    </span>
  );
}

function ThreadView({
  ticketItem, thread, loading, replyDraft, setReplyDraft,
  sendReply, sending, closeTicket, insertCanned, canReply, onReplyKey, threadEndRef,
  pendingImages = [], uploadImages, removePendingImage, uploading,
}) {
  if (!ticketItem && !thread) {
    return (
      <div className="qview qview-empty">
        <div className="qview-empty-inner">
          <Icon name="queries" size={28} />
          <p>Select a ticket on the left to view the conversation.</p>
        </div>
      </div>
    );
  }
  const t = thread?.ticket || ticketItem;
  const user = t?.user;
  const messages = thread?.messages || [];
  const isClosed = t?.status === 'CLOSED';

  // Group consecutive messages from the same sender within ~5min into
  // visual "clusters". Each cluster gets a single sender label + a
  // timestamp at the tail; intermediate bubbles render tight against
  // each other so the eye groups them as one statement. Date dividers
  // slot in whenever the calendar day changes between two messages.
  const clusters = useMemo(() => groupMessages(messages), [messages]);

  return (
    <div className="qview qview-chat">
      <div className="qview-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2>{t?.subject || '(No subject)'}</h2>
          <div className="qview-from">
            {user && (
              <>
                <CountryFlag code={user.country} width={14} />{' '}
                <b>{user.name || user.email}</b>
                {user.name && user.email && <> · <span className="mono">{user.email}</span></>}
                <> · <span className="mono">opened {agoString(t?.createdAt)}</span></>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <StatusPill status={t?.status} />
          {!isClosed && canReply && (
            <button className="btn sm danger" onClick={closeTicket} title="Close this ticket">
              <Icon name="ban" size={13} /> Close
            </button>
          )}
        </div>
      </div>

      <div className="qview-scroll">
        {loading && <div className="qview-loading">Loading conversation…</div>}
        {!loading && messages.length === 0 && (
          <div className="qview-empty-state">No messages yet.</div>
        )}
        {!loading && clusters.map((cluster, ci) => {
          // Date divider when the calendar day changes between clusters.
          const prev = ci > 0 ? clusters[ci - 1] : null;
          const showDate = !prev || dateKey(cluster.firstAt) !== dateKey(prev.lastAt);
          return (
            <React.Fragment key={cluster.id}>
              {showDate && (
                <div className="qchat-day" aria-label={prettyDay(cluster.firstAt)}>
                  <span>{prettyDay(cluster.firstAt)}</span>
                </div>
              )}
              <MessageCluster cluster={cluster} user={user} />
            </React.Fragment>
          );
        })}
        <div ref={threadEndRef} />
      </div>

      {!isClosed && canReply && (
        <div className="qreply">
          <textarea
            placeholder="Type a reply… (⌘/Ctrl+Enter to send)"
            value={replyDraft}
            onChange={(e) => setReplyDraft(e.target.value)}
            onKeyDown={onReplyKey}
            maxLength={4000}
            disabled={sending}
          />

          {/* Staged image thumbnails — admin uploaded but hasn't sent
              yet. Hover-X removes a single staged image; firing send
              ships all of them into the reply's imageUrls array. */}
          {pendingImages.length > 0 && (
            <div className="qreply-images" aria-label="Attached images">
              {pendingImages.map((url) => (
                <div key={url} className="qreply-image">
                  <img src={url} alt="" />
                  <button
                    type="button"
                    className="qreply-image-x"
                    title="Remove attachment"
                    onClick={() => removePendingImage?.(url)}
                  >×</button>
                </div>
              ))}
            </div>
          )}

          <div className="qreply-foot">
            <div className="canned">
              <button onClick={() => insertCanned("Thanks for reaching out — looking into this now and I'll come back to you within the hour.")}>
                ⏱ Looking into it
              </button>
              <button onClick={() => insertCanned("Could you share a screenshot of what you're seeing? It helps us pinpoint the issue.")}>
                📸 Ask for screenshot
              </button>
              <button onClick={() => insertCanned("This should be fixed now in the latest build. Let us know if you still see it after refreshing.")}>
                ✅ Should be fixed
              </button>
              <button onClick={() => insertCanned("I've credited your account — you should see the change immediately. Sorry for the trouble!")}>
                🎁 Comped
              </button>
              {/* Native file input wrapped as a label so we get the
                  hover/cursor affordances without re-styling the input
                  itself. Multiple-select means an admin can stage a
                  set of screenshots in one click. */}
              <label
                className={`canned-attach ${uploading ? 'busy' : ''}`}
                title="Attach images"
                style={{ cursor: uploading ? 'wait' : 'pointer' }}
              >
                {uploading ? '⏳ Uploading…' : '📎 Attach'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  disabled={uploading || sending}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    uploadImages?.(files);
                    // Reset so the same file can be re-picked later.
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <button
              className="btn primary"
              onClick={sendReply}
              disabled={sending || (!replyDraft.trim() && pendingImages.length === 0)}
            >
              <Icon name="send" size={13} />
              {sending ? 'Sending…' : 'Send reply'}
            </button>
          </div>
        </div>
      )}

      {isClosed && (
        <div style={{ padding: 14, textAlign: 'center', color: 'var(--ink-3)', borderTop: '1px solid var(--line)', fontSize: 13 }}>
          This ticket is closed. To continue the conversation, the user can open a new ticket.
        </div>
      )}

      {!canReply && !isClosed && (
        <div style={{ padding: 14, textAlign: 'center', color: 'var(--ink-3)', borderTop: '1px solid var(--line)', fontSize: 13 }}>
          Read-only role — only Support Agents and above can reply.
        </div>
      )}
    </div>
  );
}

/**
 * One cluster = a run of consecutive messages from the same sender
 * within ~5 minutes. Renders an avatar/initial on the side, the
 * sender label once at the top of the cluster, and tight-stacked
 * bubbles for each message inside. Timestamp shows on hover per
 * bubble; an explicit time also appears under the last bubble for
 * at-a-glance readability without hovering.
 */
function MessageCluster({ cluster, user }) {
  const isStaff = cluster.sender === 'STAFF';
  const senderLabel = isStaff ? 'Support team' : (user?.name || user?.email || 'User');
  const initials = (() => {
    if (isStaff) return 'HQ';
    const src = user?.name || user?.email || '?';
    return src.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('').toUpperCase() || src[0]?.toUpperCase() || '?';
  })();

  return (
    <div className={`qchat-cluster ${isStaff ? 'is-staff' : 'is-user'}`}>
      <div className="qchat-avatar" aria-hidden="true">{initials}</div>
      <div className="qchat-cluster-main">
        <div className="qchat-cluster-head">
          <span className="qchat-sender">{senderLabel}</span>
          <span className="qchat-time">{shortTime(cluster.firstAt)}</span>
        </div>
        {cluster.messages.map((m) => {
          const imgs = Array.isArray(m.imageUrls) ? m.imageUrls : [];
          return (
            <div key={m.id} className="qchat-bubble" title={fullTime(m.createdAt)}>
              {m.body ? (
                <div className="qchat-bubble-body">{m.body}</div>
              ) : null}
              {imgs.length > 0 && (
                <div className="qchat-bubble-images">
                  {imgs.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt="attachment" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Group consecutive messages from the same sender within MAX_GAP_MS
 * into clusters. Older messages first (server already orders this
 * way). Each cluster carries firstAt/lastAt so the surrounding view
 * can decide whether to insert a date divider before it.
 */
function groupMessages(messages) {
  if (!messages || messages.length === 0) return [];
  const MAX_GAP_MS = 5 * 60_000;
  const out = [];
  let current = null;
  for (const m of messages) {
    const at = new Date(m.createdAt).getTime();
    if (current
        && current.sender === m.sender
        && (at - current.lastAtMs) < MAX_GAP_MS) {
      current.messages.push(m);
      current.lastAt = m.createdAt;
      current.lastAtMs = at;
    } else {
      current = {
        id: `c-${m.id}`,
        sender: m.sender,
        firstAt: m.createdAt,
        lastAt:  m.createdAt,
        lastAtMs: at,
        messages: [m],
      };
      out.push(current);
    }
  }
  return out;
}

// ── Date / time helpers ───────────────────────────────────────────

/** "2024-01-09" — used to detect day boundaries between clusters. */
function dateKey(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "Today" / "Yesterday" / "Tue, 9 Jan" — divider label. */
function prettyDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yest  = new Date(); yest.setDate(today.getDate() - 1);
  if (dateKey(iso) === dateKey(today.toISOString())) return 'Today';
  if (dateKey(iso) === dateKey(yest.toISOString()))  return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}

/** "14:32" or "2:32 PM" depending on locale. */
function shortTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Full timestamp for the bubble `title` hover. */
function fullTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

// ── Local helpers ──────────────────────────────────────────────
function initials(name) {
  if (!name) return '—';
  return name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}
function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30) return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}
